"use client"

import { parseMappingValue } from "@/lib/parse-mapping-value"
import React, { useEffect, useMemo, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/blades/blade-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectItemText, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Plus, X } from "lucide-react"
import { toast } from "sonner"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useValueMappingSets } from "@/hooks/use-value-mapping-sets"
import { useAtsInfo } from "@/hooks/use-ats-info"
import { useTenantAtsSystem } from "@/hooks/use-explorer"
import { DestinationValueInput } from "@/components/shared/destination-value-input"
import { ValueMappingDisplay } from "@/components/shared/value-mapping-display"
import { ArrayPathRefiner } from "@/components/shared/array-path-refiner"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type {
  AtsDestinationFieldInfo,
  FieldMapping,
  MappingConfig,
  ValueMapping,
  ValueMappingSet,
} from "@/types/fbo-api"

interface AddToMappingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The clicked field path (AtsFieldMapping.SourceField notation) */
  sourceFieldPath: string
  /** The entity type currently being explored — used to preselect the matching mapping entity */
  exploredEntityType: string | null
}

/**
 * Appends the clicked explorer field as a sourceField on an existing ATS -> FBO
 * mapping configuration: pick the mapping type, the entity within it, and the
 * FBO destination field — with the same optional Data Type and Value Mappings
 * (inline or shared set) sections as the mapping editor's Add Mapping dialog.
 */
export function AddToMappingDialog({ open, onOpenChange, sourceFieldPath, exploredEntityType }: AddToMappingDialogProps) {
  const { loadMappingNames, loadConfig, loadAvailableDestinationFields, saveConfig } = useMappingConfig()
  const { loadSets } = useValueMappingSets()
  const { atsSource } = useTenantAtsSystem()
  const atsInfoState = useAtsInfo(open ? atsSource : null)
  const { t, n } = useTranslation()

  const [mappingNames, setMappingNames] = useState<string[]>([])
  const [selectedMappingName, setSelectedMappingName] = useState<string>("")
  // The clicked path, refinable when it traverses an array (item selection / JSONPath filter)
  const [sourceField, setSourceField] = useState(sourceFieldPath)
  const [config, setConfig] = useState<MappingConfig | null>(null)
  const [destinationFields, setDestinationFields] = useState<AtsDestinationFieldInfo[]>([])
  const [availableSets, setAvailableSets] = useState<ValueMappingSet[]>([])
  const [selectedEntityName, setSelectedEntityName] = useState<string>("")
  const [selectedDestinationField, setSelectedDestinationField] = useState<string>("")
  const [dataTypeConverter, setDataTypeConverter] = useState<string | null>(null)
  const [valueMappings, setValueMappings] = useState<ValueMapping[]>([])
  const [valueMappingSetName, setValueMappingSetName] = useState<string | null>(null)
  const [defaultValueMapping, setDefaultValueMapping] = useState("")
  const [newSourceValue, setNewSourceValue] = useState("")
  const [newDestinationValue, setNewDestinationValue] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    loadMappingNames()
      .then(setMappingNames)
      .catch((err) => setError(err instanceof Error ? err.message : t("explorer.mappingCommon.errors.loadTypes")))
    loadSets()
      .then(setAvailableSets)
      .catch(() => setAvailableSets([]))
    // Reset the optional sections for each new field being added
    setSourceField(sourceFieldPath)
    setDataTypeConverter(null)
    setValueMappings([])
    setValueMappingSetName(null)
    setDefaultValueMapping("")
    setNewSourceValue("")
    setNewDestinationValue("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!selectedMappingName) {
      setConfig(null)
      setDestinationFields([])
      return
    }

    setIsLoading(true)
    setError(null)
    Promise.all([loadConfig(selectedMappingName), loadAvailableDestinationFields(selectedMappingName)])
      .then(([loadedConfig, fields]) => {
        setConfig(loadedConfig)
        setDestinationFields(fields)

        // Preselect the entity whose entityType matches what's being explored
        const matching = loadedConfig.entities?.find(
          (e) => e.entityType?.toLowerCase() === exploredEntityType?.toLowerCase()
        )
        setSelectedEntityName(matching?.name ?? loadedConfig.entities?.[0]?.name ?? "")
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("explorer.mappingCommon.errors.loadConfig")))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMappingName])

  const mappedDestinationFields = useMemo(() => {
    const used = new Set(
      config?.entities?.flatMap((e) => e.fieldMappings?.map((m) => m.destinationField) ?? []) ?? []
    )
    return new Set(used)
  }, [config])

  const selectedFieldInfo = useMemo(
    () => destinationFields.find((f) => f.FieldPath === selectedDestinationField) ?? null,
    [destinationFields, selectedDestinationField]
  )
  const codeTableName = selectedFieldInfo?.CodeTable?.Name ?? null
  const codeTableValueType = selectedFieldInfo?.CodeTable?.ValueType ?? null

  const selectedSet = useMemo(
    () => availableSets.find((s) => s.name === valueMappingSetName) ?? null,
    [availableSets, valueMappingSetName]
  )

  const selectedConverterDescription = useMemo(() => {
    if (!dataTypeConverter || !atsInfoState.data?.supportedDataTypeConverters) return null
    return atsInfoState.data.supportedDataTypeConverters.find((c) => c.name === dataTypeConverter)?.description ?? null
  }, [dataTypeConverter, atsInfoState.data?.supportedDataTypeConverters])

  const handleAddValueMapping = () => {
    if (!newSourceValue || !newDestinationValue) {
      toast.error(t("explorer.addField.errors.valuesRequired"))
      return
    }
    setValueMappings([
      ...valueMappings,
      { sourceValue: parseMappingValue(newSourceValue, true), destinationValue: parseMappingValue(newDestinationValue, true) },
    ])
    setNewSourceValue("")
    setNewDestinationValue("")
  }

  const handleSave = async () => {
    if (!config || !selectedEntityName || !selectedDestinationField) return

    const entity = config.entities.find((e) => e.name === selectedEntityName)
    if (!entity) return

    const newMapping: FieldMapping = {
      sourceField,
      destinationField: selectedDestinationField,
    }
    if (dataTypeConverter) {
      newMapping.dataTypeConverter = dataTypeConverter
    }
    if (valueMappingSetName) {
      newMapping.valueMappingSetName = valueMappingSetName
    } else if (valueMappings.length > 0) {
      newMapping.valueMappings = valueMappings
    }
    const parsedDefault = defaultValueMapping.trim() === "" ? null : parseMappingValue(defaultValueMapping)
    if (parsedDefault !== null && typeof parsedDefault !== "boolean") {
      newMapping.defaultValueMapping = parsedDefault
    }

    setIsSaving(true)
    try {
      entity.fieldMappings = [...(entity.fieldMappings ?? []), newMapping]

      const success = await saveConfig(selectedMappingName, config)
      if (success) {
        toast.success(t("explorer.addField.saved", { source: sourceField, destination: selectedDestinationField, mapping: selectedMappingName }))
        onOpenChange(false)
        setSelectedDestinationField("")
      } else {
        setError(t("explorer.mappingCommon.errors.save"))
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("explorer.addField.title")}</DialogTitle>
          <DialogDescription>
            {t("explorer.addField.descriptionBefore")} <code className="bg-muted px-1 rounded">{sourceField}</code>{" "}
            {t("explorer.addField.descriptionAfter")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label>{t("explorer.mappingCommon.type")}</Label>
            <Select value={selectedMappingName} onValueChange={setSelectedMappingName}>
              <SelectTrigger>
                <SelectValue placeholder={t("explorer.mappingCommon.selectType")} />
              </SelectTrigger>
              <SelectContent>
                {mappingNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("explorer.mappingCommon.loadingConfig")}
            </div>
          )}

          {config && config.entities.length > 0 && (
            <div className="space-y-2">
              <Label>{t("explorer.addField.entity")}</Label>
              <Select value={selectedEntityName} onValueChange={setSelectedEntityName}>
                <SelectTrigger>
                  <SelectValue placeholder={t("explorer.addField.selectEntity")} />
                </SelectTrigger>
                <SelectContent>
                  {config.entities.map((entity) => (
                    <SelectItem key={entity.name} value={entity.name}>
                      {entity.name} ({entity.entityType})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t("explorer.addField.pathNote")}</p>
              {sourceField.includes("[") && selectedEntityName && (
                <ArrayPathRefiner
                  entityType={
                    config.entities.find((e) => e.name === selectedEntityName)?.entityType ?? ""
                  }
                  path={sourceField}
                  onChange={setSourceField}
                />
              )}
            </div>
          )}

          {config && config.entities.length === 0 && (
            <Alert>
              <AlertDescription>{t("explorer.addField.noEntities")}</AlertDescription>
            </Alert>
          )}

          {destinationFields.length > 0 && (
            <div className="space-y-2">
              <Label>{t("explorer.addField.destinationField")}</Label>
              <Select value={selectedDestinationField} onValueChange={setSelectedDestinationField}>
                <SelectTrigger>
                  <SelectValue placeholder={t("explorer.addField.selectDestinationField")} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {destinationFields.map((field) => (
                    <SelectItem key={field.FieldPath} value={field.FieldPath}>
                      {field.DisplayName}
                      {mappedDestinationFields.has(field.FieldPath) ? ` ${t("explorer.addField.alreadyMapped")}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedDestinationField && mappedDestinationFields.has(selectedDestinationField) && (
                <p className="text-sm text-amber-600">
                  {t("explorer.addField.alreadyMappedWarning", { field: selectedDestinationField })}
                </p>
              )}
            </div>
          )}

          {config && selectedDestinationField && (
            <>
              <div className="space-y-2">
                <Label>{t("explorer.addField.dataType")}</Label>
                <Select
                  value={dataTypeConverter ?? "none"}
                  onValueChange={(value) => setDataTypeConverter(value === "none" ? null : value)}
                  disabled={atsInfoState.isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={atsInfoState.isLoading ? t("common.state.loading") : t("explorer.addField.selectDataType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("common.state.none")}</SelectItem>
                    {atsInfoState.data?.supportedDataTypeConverters?.map((converter) => (
                      <SelectItem key={converter.name} value={converter.name}>
                        <div className="flex flex-col">
                          <SelectItemText className="font-medium">{converter.name}</SelectItemText>
                          <span className="text-xs text-muted-foreground">{converter.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedConverterDescription && (
                  <p className="text-sm text-muted-foreground">{selectedConverterDescription}</p>
                )}
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium">{t("explorer.addField.valueMappings")}</h4>
                <p className="text-sm text-muted-foreground">{t("explorer.addField.valueMappingsHint")}</p>

                {codeTableName && (
                  <div className="p-3 bg-muted rounded-md border">
                    <p className="text-sm font-medium">
                      {t("explorer.addField.linkedCodeTable")}{" "}
                      <code className="px-1 py-0.5 bg-background rounded-sm">{codeTableName}</code>
                    </p>
                  </div>
                )}

                {availableSets.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t("explorer.addField.sharedSet")}</Label>
                    <Select
                      value={valueMappingSetName ?? "none"}
                      onValueChange={(value) => setValueMappingSetName(value === "none" ? null : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("explorer.addField.noneInline")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("explorer.addField.noneInline")}</SelectItem>
                        {availableSets.map((set) => (
                          <SelectItem key={set.name} value={set.name}>
                            {set.name} ({t("explorer.addField.setValues", { count: n(set.valueMappings?.length ?? 0) })})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedSet && (
                  <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                    {(selectedSet.valueMappings ?? []).map((valueMapping, index) => (
                      <ValueMappingDisplay
                        key={index}
                        sourceValue={valueMapping.sourceValue}
                        destinationValue={valueMapping.destinationValue}
                        codeTableName={selectedSet.fboCodeTableName ?? codeTableName}
                        codeTableValueType={selectedSet.fboCodeTableValueType ?? codeTableValueType}
                        className="font-mono bg-muted px-2 py-1 rounded"
                      />
                    ))}
                    {(selectedSet.valueMappings?.length ?? 0) === 0 && (
                      <p className="text-sm text-muted-foreground italic">{t("explorer.addField.setEmpty")}</p>
                    )}
                  </div>
                )}

                {!valueMappingSetName && (
                  <>
                    {valueMappings.length > 0 && (
                      <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                        {valueMappings.map((valueMapping, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <ValueMappingDisplay
                              sourceValue={valueMapping.sourceValue}
                              destinationValue={valueMapping.destinationValue}
                              codeTableName={codeTableName}
                              codeTableValueType={codeTableValueType}
                              className="font-mono bg-muted px-2 py-1 rounded"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setValueMappings(valueMappings.filter((_, i) => i !== index))}
                              className="h-6 w-6"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2 items-end">
                      <div className="space-y-2">
                        <Label htmlFor="explorerNewSourceValue">{t("explorer.addField.sourceValue")}</Label>
                        <Input
                          id="explorerNewSourceValue"
                          placeholder={t("explorer.addField.sourceValuePlaceholder")}
                          value={newSourceValue}
                          onChange={(e) => setNewSourceValue(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="explorerNewDestinationValue">{t("explorer.addField.destinationValue")}</Label>
                        <DestinationValueInput
                          value={newDestinationValue}
                          onValueChange={setNewDestinationValue}
                          codeTableName={codeTableName}
                          codeTableValueType={codeTableValueType}
                          placeholder={t("explorer.addField.destinationValuePlaceholder")}
                          id="explorerNewDestinationValue"
                        />
                      </div>
                      <Button onClick={handleAddValueMapping} size="sm">
                        <Plus className="h-4 w-4 mr-2" /> {t("explorer.addField.add")}
                      </Button>
                    </div>
                  </>
                )}

              </div>

              <Separator />

              {/* Its own section, not part of Value Mappings: the default answers two independent
                  questions — nothing arrived, and the value that arrived matched no row — and a field
                  can want the first without having a table at all. Nesting it under the table, and
                  rendering it only once a row existed, made the "nothing arrived" case unreachable. */}
              <div className="space-y-3">
                <h4 className="font-medium">{t("explorer.addField.defaultValue")}</h4>
                <div className="space-y-2">
                  <Label htmlFor="explorerDefaultValueMapping">{t("explorer.addField.defaultValueMapping")}</Label>
                  <DestinationValueInput
                    value={defaultValueMapping}
                    onValueChange={setDefaultValueMapping}
                    codeTableName={codeTableName}
                    codeTableValueType={codeTableValueType}
                    placeholder={t("explorer.addField.defaultPlaceholder")}
                    id="explorerDefaultValueMapping"
                  />
                  <p className="text-sm text-muted-foreground">{t("explorer.addField.defaultHint")}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isSaving ||
              !config ||
              !selectedEntityName ||
              !selectedDestinationField ||
              mappedDestinationFields.has(selectedDestinationField)
            }
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("explorer.addField.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
